import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import {
  Inbox as InboxIcon,
  SearchOff as SearchOffIcon,
  EventBusy as EventBusyIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

const EmptyState = ({
  icon: CustomIcon,
  title,
  description,
  action,
  actionLabel,
  variant = 'default', // default, search, error
  sx = {},
}) => {
  // Default icons based on variant
  const getDefaultIcon = () => {
    switch (variant) {
      case 'search':
        return SearchOffIcon;
      case 'error':
        return ErrorIcon;
      case 'events':
        return EventBusyIcon;
      default:
        return InboxIcon;
    }
  };

  const Icon = CustomIcon || getDefaultIcon();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 6,
        textAlign: 'center',
        bgcolor: 'transparent',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 120,
          height: 120,
          borderRadius: '50%',
          bgcolor: 'grey.100',
          mb: 3,
        }}
      >
        <Icon
          sx={{
            fontSize: 64,
            color: 'grey.400',
          }}
        />
      </Box>

      <Typography
        variant="h5"
        gutterBottom
        fontWeight={600}
        color="text.primary"
        sx={{ mb: 1 }}
      >
        {title || 'No items found'}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
      >
        {description || 'There are no items to display at the moment.'}
      </Typography>

      {action && actionLabel && (
        <Button
          variant="contained"
          onClick={action}
          size="large"
        >
          {actionLabel}
        </Button>
      )}
    </Paper>
  );
};

export default EmptyState;

// Preset Empty States for common scenarios
export const EmptyEvents = ({ action, actionLabel }) => (
  <EmptyState
    variant="events"
    title="No Events Found"
    description="There are currently no events available. Check back later or create a new event to get started."
    action={action}
    actionLabel={actionLabel}
  />
);

export const EmptySearch = ({ searchTerm, onClear }) => (
  <EmptyState
    variant="search"
    title="No Results Found"
    description={
      searchTerm
        ? `We couldn't find any results for "${searchTerm}". Try adjusting your search or filters.`
        : 'Try adjusting your search or filters to find what you\'re looking for.'
    }
    action={onClear}
    actionLabel="Clear Filters"
  />
);

export const EmptyRSVPs = ({ action, actionLabel }) => (
  <EmptyState
    variant="events"
    title="No RSVPs Yet"
    description="You haven't RSVP'd to any events yet. Browse available events and register to get started."
    action={action}
    actionLabel={actionLabel || 'Browse Events'}
  />
);

export const EmptyAttendees = () => (
  <EmptyState
    variant="default"
    title="No Attendees"
    description="This event doesn't have any registered attendees yet. Share the event to get more registrations."
  />
);

export const ErrorState = ({ message, action, actionLabel }) => (
  <EmptyState
    variant="error"
    title="Something Went Wrong"
    description={message || 'An error occurred while loading the data. Please try again.'}
    action={action}
    actionLabel={actionLabel || 'Retry'}
  />
);
